/* pl-announcement-bar.js
   Announcement bar rotation controls (prev / next / play-pause, keyboard,
   focus and hover pausing) and the in-bar promo countdown. Externalized from
   two inline <script> blocks in sections/announcement-bar.liquid that ran
   during HTML parse at the top of <body> on every page.

   Config comes from the markup the section already renders:
     <section data-pl-annc>                            root, one per section
     announcement-bar                                  theme custom element
     [data-action="annc-prev|annc-next|annc-playpause"] controls
     .annc-countdown .cart-countdown-timer[data-expiry][data-prefix]
     .annc-countdown[data-enhanced]

   PL 2026-10-24: also replaces the theme's message transition with a
   gentle horizontal slide (see "slide transition" below).

   Idempotent per root (data-pl-annc-init) and theme-editor aware:
   shopify:section:load re-initializes a re-rendered section, and
   shopify:section:unload clears its countdown interval. */
(function () {
  'use strict';

  var ROOT_SEL = '[data-pl-annc]';

  /* ---------------- rotation controls ---------------- */
  function initControls(root) {
    var bar = root.querySelector('announcement-bar');
    if (!bar) return;

    var playPauseBtn = root.querySelector('[data-action="annc-playpause"]');
    var prevBtn = root.querySelector('[data-action="annc-prev"]');
    var nextBtn = root.querySelector('[data-action="annc-next"]');
    var labelEl = playPauseBtn ? playPauseBtn.querySelector('[data-playpause-label]') : null;

    var hasAutoplay = !!playPauseBtn;
    var userPaused = false;
    var focusPaused = false;
    var ready = false;

    function syncLabel() {
      if (!playPauseBtn) return;
      var paused = playPauseBtn.getAttribute('aria-pressed') === 'true';
      var label = paused ? 'Play announcement rotation' : 'Pause announcement rotation';
      playPauseBtn.setAttribute('aria-label', label);
      if (labelEl) labelEl.textContent = label;
    }

    customElements.whenDefined('announcement-bar').then(function () {
      if (typeof bar._pausePlayer !== 'function' || typeof bar._startPlayer !== 'function') {
        ready = hasAutoplay ? false : true;
        return;
      }
      ready = true;
    });

    function pause() {
      if (!ready || !hasAutoplay) return;
      try { bar._pausePlayer(); } catch (e) {}
    }
    function play() {
      if (!ready || !hasAutoplay) return;
      if (userPaused) return;
      try {
        bar._pausePlayer();
        bar._startPlayer();
      } catch (e) {}
    }

    /* Mark user as manually in control after prev/next click.
       The theme's built-in select() unconditionally restarts the interval
       when it finishes, so we defensively re-pause once the transition settles. */
    function markUserPaused() {
      if (!hasAutoplay) return;
      userPaused = true;
      if (playPauseBtn) {
        playPauseBtn.setAttribute('aria-pressed', 'true');
        syncLabel();
      }
      /* Kill the auto-restart triggered by the internal select() promise.
         The transition typically resolves inside ~500ms; 900ms is a safe margin
         and still well under the minimum cycle_speed of 3000ms. */
      setTimeout(pause, 900);
      setTimeout(pause, 1600);
    }

    function goPrev() {
      if (typeof bar.previous !== 'function') return;
      markUserPaused();
      try { bar.previous(); } catch (e) {}
    }
    function goNext() {
      if (typeof bar.next !== 'function') return;
      markUserPaused();
      try { bar.next(); } catch (e) {}
    }

    /* We intentionally bind our own click handlers instead of relying on the
       built-in delegate for [data-action="prev"|"next"], so we can mark
       userPaused before the internal select() fires. */
    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        goPrev();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        goNext();
      });
    }

    /* Keyboard: Left/Right arrows when focus is on any control inside the bar. */
    bar.addEventListener('keydown', function (e) {
      var t = e.target;
      if (!t || (t !== prevBtn && t !== nextBtn && t !== playPauseBtn)) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
        if (prevBtn) prevBtn.focus();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
        if (nextBtn) nextBtn.focus();
      }
    });

    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', function () {
        userPaused = !userPaused;
        playPauseBtn.setAttribute('aria-pressed', userPaused ? 'true' : 'false');
        syncLabel();
        if (userPaused) {
          pause();
        } else if (!focusPaused) {
          play();
        }
      });

      bar.addEventListener('focusin', function () {
        focusPaused = true;
        pause();
      });
      bar.addEventListener('focusout', function (e) {
        if (bar.contains(e.relatedTarget)) return;
        focusPaused = false;
        if (!userPaused) play();
      });
      bar.addEventListener('mouseenter', function () {
        focusPaused = true;
        pause();
      });
      bar.addEventListener('mouseleave', function () {
        focusPaused = false;
        if (!userPaused) play();
      });

      syncLabel();
    }
  }

  /* ---------------- countdown ---------------- */
  // PL 2026-10-20: at the end time the countdown is removed from view instead of
  // printing "Promo expired". A dead timer in the bar reads as broken, and the
  // master switch already stops the promo pricing at the same second.
  function setExpired(containers) {
    containers.forEach(function (container) {
      container.hidden = true;
      container.style.display = 'none';
    });
  }

  function pad2(n) {
    return n.toString().padStart(2, '0');
  }

  function initCountdown(root) {
    var containers = Array.prototype.slice.call(root.querySelectorAll('.annc-countdown'));
    if (!containers.length) return;

    var first = containers[0];
    var timerEl = first.querySelector('.cart-countdown-timer');
    var expiryRaw = timerEl ? (timerEl.getAttribute('data-expiry') || '').trim() : '';
    var prefix = timerEl ? (timerEl.getAttribute('data-prefix') || 'Ends') : 'Ends';
    var isEnhanced = first.getAttribute('data-enhanced') === 'true';

    if (!expiryRaw) { setExpired(containers); return; }

    var expiry = new Date(expiryRaw);
    if (isNaN(expiry.getTime())) { setExpired(containers); return; }

    var handle = null;

    function updateAll() {
      var diff = expiry.getTime() - Date.now();

      if (diff <= 0) {
        setExpired(containers);
        if (handle) clearInterval(handle);
        return;
      }

      var d = Math.floor(diff / 86400000),
          h = Math.floor((diff % 86400000) / 3600000),
          m = Math.floor((diff % 3600000) / 60000),
          s = Math.floor((diff % 60000) / 1000);

      var isSubday = diff < 86400000;
      containers.forEach(function (container) {
        container.classList.toggle('is-subday', isSubday);
      });

      var html;
      if (isEnhanced) {
        html =
          '<span class="countdown-prefix">' + prefix + '</span>' +
          '<span class="countdown-boxes">' +
            '<span class="countdown-box countdown-box--days">' +
              '<span class="countdown-value">' + pad2(d) + '</span>' +
              '<span class="countdown-label">days</span>' +
            '</span>' +
            '<span class="countdown-box">' +
              '<span class="countdown-value">' + pad2(h) + '</span>' +
              '<span class="countdown-label">hrs</span>' +
            '</span>' +
            '<span class="countdown-box">' +
              '<span class="countdown-value">' + pad2(m) + '</span>' +
              '<span class="countdown-label">min</span>' +
            '</span>' +
            '<span class="countdown-box">' +
              '<span class="countdown-value">' + pad2(s) + '</span>' +
              '<span class="countdown-label">sec</span>' +
            '</span>' +
          '</span>';
      } else if (isSubday) {
        /* Zero-padded so the string width never changes between ticks -
           an unpadded "9m 5s" -> "10m 0s" width jitter can toggle mobile
           wrapping at the threshold and register a layout shift every
           second. Pairs with font-variant-numeric: tabular-nums and the
           .annc-countdown__ph placeholder in the section CSS. */
        html = '<span class="countdown-prefix">' + prefix + '</span> ' + pad2(h) + 'h ' + pad2(m) + 'm ' + pad2(s) + 's';
      } else {
        html = '<span class="countdown-prefix">' + prefix + '</span> ' + pad2(d) + 'd ' + pad2(h) + 'h ' + pad2(m) + 'm ' + pad2(s) + 's';
      }

      containers.forEach(function (container) {
        var timer = container.querySelector('.cart-countdown-timer');
        if (timer) timer.innerHTML = html;
      });
    }

    updateAll();
    handle = setInterval(updateAll, 1000);
    root._plAnncCountdown = handle;
  }

  /* ---------------- slide transition ---------------- */
  /* PL 2026-10-24: messages slide in from the right and out to the left
     (24px, with a fade) instead of the theme's 8px, 150ms upward nudge, which
     was too subtle to notice. Visitors with "reduce motion" turned on get a
     plain crossfade. Overrides the theme's announcement-bar-item select and
     deselect; everything else (timing, pause, prev/next) is unchanged. */
  function patchSlide() {
    var Item = customElements.get('announcement-bar-item');
    if (!Item || Item.prototype.__plSlide) return;
    Item.prototype.__plSlide = true;

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    var EASE = 'cubic-bezier(0.2, 0.7, 0.2, 1)';

    function run(el, frames, duration) {
      return new Promise(function (resolve) {
        if (!duration || typeof el.animate !== 'function') { resolve(); return; }
        var a = el.animate(frames, { duration: duration, easing: EASE });
        a.onfinish = resolve;
        a.oncancel = resolve;
      });
    }

    Item.prototype.select = function (animate) {
      if (animate === undefined) animate = true;
      this.removeAttribute('hidden');
      if (reduce.matches) {
        return run(this, { opacity: [0, 1] }, animate ? 200 : 0);
      }
      return run(this, { transform: ['translateX(24px)', 'translateX(0)'], opacity: [0, 1] }, animate ? 350 : 0);
    };

    Item.prototype.deselect = function (animate) {
      var self = this;
      if (animate === undefined) animate = true;
      var closing = typeof self.closeContent === 'function' ? self.closeContent() : null;
      return Promise.resolve(closing).then(function () {
        if (reduce.matches) {
          return run(self, { opacity: [1, 0] }, animate ? 150 : 0);
        }
        return run(self, { transform: ['translateX(0)', 'translateX(-24px)'], opacity: [1, 0] }, animate ? 250 : 0);
      }).then(function () {
        self.setAttribute('hidden', '');
      });
    };
  }

  if (window.customElements) {
    customElements.whenDefined('announcement-bar-item').then(patchSlide);
  }

  /* ---------------- boot ---------------- */
  function init(root) {
    if (!root || root.hasAttribute('data-pl-annc-init')) return;
    root.setAttribute('data-pl-annc-init', '');
    initControls(root);
    initCountdown(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SEL).forEach(init);
  }

  initAll();

  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target) initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (!e || !e.target) return;
    e.target.querySelectorAll(ROOT_SEL).forEach(function (root) {
      if (root._plAnncCountdown) clearInterval(root._plAnncCountdown);
    });
  });
})();
